(() => {
  'use strict';

  if (window.GTEmptyCartBrowseLoaded) {
    return;
  }

  window.GTEmptyCartBrowseLoaded = true;

  const SELECTORS = {
    carousel: '[data-empty-cart-carousel]',
    viewport: '[data-empty-cart-viewport]',
    card: '[data-empty-cart-card]',
    counter: '[data-empty-cart-counter]',
    arrow: '[data-empty-cart-scroll]',
    addButton: '[data-empty-cart-add]',
    variant: '[data-empty-cart-variant]',
    status: '[data-empty-cart-status]',

    drawerCheckout:
      'cart-drawer #CartDrawer-Checkout, cart-drawer [data-cart-checkout]',

    cartPageCheckout:
      '#main-cart-footer [data-cart-page-checkout], #main-cart-footer #checkout',

    dispatchMessage: '[data-gt-dispatch-message]',
    dispatchText: '[data-gt-dispatch-message-text]',
  };

  const STYLE_ID = 'GTEmptyCartBrowseStyles';
  const MELBOURNE_TIME_ZONE = 'Australia/Melbourne';

  /*
   * Shopify server-clock synchronisation.
   *
   * The browser still uses its own clock as an immediate
   * fallback, but a same-origin request to Shopify's
   * locale-aware cart.js endpoint provides the HTTP Date
   * response header. The difference between that server time
   * and the customer's device time is then applied to every
   * dispatch calculation.
   */
  const SERVER_CLOCK_RESYNC_INTERVAL_MS =
    15 * 60 * 1000;

  const SERVER_CLOCK_MAX_AGE_MS =
    5 * 60 * 1000;

  /*
   * Confirmed Victorian public holidays for 2026 and 2027.
   *
   * Weekend holidays are included for completeness, even
   * though Saturday and Sunday are already non-dispatch days.
   *
   * 3 January 2028 is included so an order placed on
   * Friday 31 December 2027 correctly says:
   *
   * Ships Tuesday
   */
  const CONFIRMED_PUBLIC_HOLIDAYS = new Set([
    /* 2026 */
    '2026-01-01',
    '2026-01-26',
    '2026-03-09',
    '2026-04-03',
    '2026-04-04',
    '2026-04-05',
    '2026-04-06',
    '2026-04-25',
    '2026-06-08',
    '2026-09-25',
    '2026-11-03',
    '2026-12-25',
    '2026-12-26',
    '2026-12-28',

    /* 2027 */
    '2027-01-01',
    '2027-01-26',
    '2027-03-08',
    '2027-03-26',
    '2027-03-27',
    '2027-03-28',
    '2027-03-29',
    '2027-04-25',
    '2027-06-14',
    '2027-11-02',
    '2027-12-25',
    '2027-12-26',
    '2027-12-27',
    '2027-12-28',

    /* Needed for the end of 2027 */
    '2028-01-01',
    '2028-01-03',
  ]);

  /*
   * PROVISIONAL DATE
   *
   * Victoria has not yet officially confirmed the 2027
   * Friday before the AFL Grand Final.
   *
   * It normally falls on the final Friday in September,
   * which is 24 September in 2027.
   *
   * Change this one value later if the official date differs.
   */
  const PROVISIONAL_PUBLIC_HOLIDAYS = new Set([
    '2027-09-24',
  ]);

  const PUBLIC_HOLIDAYS = new Set([
    ...CONFIRMED_PUBLIC_HOLIDAYS,
    ...PROVISIONAL_PUBLIC_HOLIDAYS,
  ]);

  const MELBOURNE_DATE_TIME_FORMATTER =
    new Intl.DateTimeFormat('en-AU', {
      timeZone: MELBOURNE_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

  const WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  let resizeTimer = null;
  let dispatchTimer = null;
  let serverClockResyncTimer = null;

  let serverClockBaseTimeMs = 0;
  let serverClockBasePerformanceMs = 0;
  let serverClockLastSyncedPerformanceMs = 0;
  let serverClockSyncPromise = null;
  let serverClockSynchronised = false;

  /* ======================================================
     SHARED STYLES
     ====================================================== */

  function injectStyles() {
    const existingStyle =
      document.getElementById(STYLE_ID);

    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');

    style.id = STYLE_ID;

    style.textContent = `
      /* ==================================================
         EMPTY CART BODY
         ================================================== */

      html body cart-drawer.is-empty .drawer__inner {
        background: #ffffff !important;
      }

      html body cart-drawer.is-empty .drawer__inner-empty {
        background: #ffffff !important;
      }

      html body cart-drawer.is-empty
        .cart-drawer__empty-content {
        padding:
          22px
          0
          calc(28px + env(safe-area-inset-bottom)) !important;
      }

      /* ==================================================
         EMPTY CART HERO
         ================================================== */

      html body cart-drawer.is-empty
        .gt-empty-cart-hero {
        width: auto !important;
        margin: 0 18px 22px !important;
        padding: 22px 20px 24px !important;
        background: #ffffff !important;
        border: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        text-align: center !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-hero__icon {
        width: 64px !important;
        height: 64px !important;
        margin: 0 auto 13px !important;
        color: #1d5637 !important;
        background: #eef1eb !important;
        border: 0 !important;
        box-shadow: none !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-hero__icon svg {
        width: 31px !important;
        height: 31px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-hero__heading {
        margin: 0 !important;
        color: #353027 !important;
        font-size: 24px !important;
        font-weight: 700 !important;
        line-height: 1.2 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-hero__text {
        max-width: 350px !important;
        margin: 9px auto 17px !important;
        color: #66625d !important;
        font-size: 16px !important;
        line-height: 1.45 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-hero__button.button {
        min-width: 220px !important;
        min-height: 48px !important;
        padding: 0 22px !important;
        color: #ffffff !important;
        background: #1d5637 !important;
        border: 0 !important;
        border-radius: 8px !important;
        box-shadow: none !important;
        font-size: 16px !important;
        font-weight: 700 !important;
        letter-spacing: 0 !important;
        text-transform: none !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-hero__button.button:hover {
        color: #ffffff !important;
        background: #17472e !important;
      }

      /* ==================================================
         EMPTY CART RECOMMENDATION SECTIONS
         ================================================== */

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel {
        width: 100% !important;
        margin: 0 !important;
        padding: 20px 18px 18px !important;
        overflow: hidden !important;
        color: #353027 !important;
        background: #f4f4f4 !important;
        border: 0 !important;
        border-top: 1px solid #e5e5e5 !important;
        border-bottom: 1px solid #e5e5e5 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel
        + .gt-empty-cart-carousel {
        margin-top: 14px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__header {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 12px !important;
        margin: 0 0 15px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__heading-group {
        min-width: 0 !important;
        padding: 0 !important;
        border: 0 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__heading {
        margin: 0 !important;
        color: #353027 !important;
        font-size: 20px !important;
        font-weight: 700 !important;
        line-height: 1.2 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__subheading {
        margin: 4px 0 0 !important;
        color: #66625d !important;
        font-size: 13px !important;
        line-height: 1.3 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__controls {
        display: flex !important;
        flex: 0 0 auto !important;
        align-items: center !important;
        gap: 4px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__counter {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-width: 58px !important;
        min-height: 34px !important;
        padding: 5px 10px !important;
        color: #353027 !important;
        background: #ffffff !important;
        border: 1px solid #dedede !important;
        border-radius: 999px !important;
        box-shadow: none !important;
        font-size: 14px !important;
        font-weight: 700 !important;
        font-variant-numeric: tabular-nums !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__arrow {
        display: grid !important;
        place-items: center !important;
        width: 35px !important;
        min-width: 35px !important;
        height: 35px !important;
        min-height: 35px !important;
        margin: 0 !important;
        padding: 0 !important;
        color: #353027 !important;
        background: transparent !important;
        border: 0 !important;
        border-radius: 50% !important;
        box-shadow: none !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__arrow:hover:not(:disabled) {
        color: #1d5637 !important;
        background: rgba(29, 86, 55, 0.07) !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__arrow:disabled {
        color: #9a9894 !important;
        opacity: 0.4 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__arrow svg {
        width: 11px !important;
        height: 7px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__viewport {
        width: 100% !important;
        overflow-x: auto !important;
        overflow-y: visible !important;
        scroll-behavior: smooth !important;
        scroll-snap-type: x proximity !important;
        overscroll-behavior-x: contain !important;
        overscroll-behavior-y: auto !important;
        scrollbar-width: none !important;
        -webkit-overflow-scrolling: touch !important;
        touch-action: pan-x pan-y pinch-zoom !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__viewport *,
      html body cart-drawer.is-empty
        .gt-empty-cart-card {
        touch-action: pan-x pan-y pinch-zoom !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__viewport::-webkit-scrollbar {
        display: none !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__track {
        display: flex !important;
        align-items: stretch !important;
        gap: 12px !important;
        width: max-content !important;
      }

      /* ==================================================
         EMPTY CART PRODUCT CARDS
         ================================================== */

      html body cart-drawer.is-empty
        .gt-empty-cart-card {
        flex: 0 0 calc(100% - 34px) !important;
        display: grid !important;
        grid-template-columns:
          82px
          minmax(0, 1fr)
          auto !important;
        grid-template-rows:
          auto
          auto !important;
        align-items: start !important;
        gap: 8px 12px !important;
        width: calc(100% - 34px) !important;
        max-width: 390px !important;
        min-width: 310px !important;
        min-height: 108px !important;
        margin: 0 !important;
        padding: 12px !important;
        color: #353027 !important;
        background: #ffffff !important;
        border:
          1px solid rgba(53, 48, 39, 0.08) !important;
        border-radius: 12px !important;
        box-shadow: none !important;
        scroll-snap-align: start !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__image-link {
        grid-column: 1 !important;
        grid-row: 1 / 3 !important;
        display: block !important;
        width: 82px !important;
        height: 82px !important;
        overflow: hidden !important;
        background: #f1eee9 !important;
        border-radius: 9px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__image {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__content {
        grid-column: 2 / 4 !important;
        grid-row: 1 !important;
        display: grid !important;
        grid-template-columns:
          minmax(0, 1fr)
          auto !important;
        align-items: start !important;
        gap: 8px !important;
        min-width: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__title {
        grid-column: 1 !important;
        display: block !important;
        min-width: 0 !important;
        margin: 0 !important;
        color: #353027 !important;
        font-size: 15px !important;
        font-weight: 500 !important;
        line-height: 1.32 !important;
        text-decoration: none !important;
        overflow-wrap: anywhere !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__title:hover {
        color: #1d5637 !important;
        text-decoration: underline !important;
        text-underline-offset: 2px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__price s {
        display: none !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__price {
        grid-column: 2 !important;
        display: block !important;
        min-width: max-content !important;
        margin: 0 !important;
        color: #353027 !important;
        line-height: 1.2 !important;
        text-align: right !important;
        white-space: nowrap !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__price strong,
      html body cart-drawer.is-empty
        .gt-empty-cart-card__selected-price {
        color: #353027 !important;
        font-size: 16px !important;
        font-weight: 700 !important;
        line-height: 1.2 !important;
        letter-spacing: 0 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__variant-area {
        grid-column: 2 !important;
        grid-row: 2 !important;
        align-self: end !important;
        min-width: 0 !important;
        margin: 0 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__variant {
        display: block !important;
        width: auto !important;
        max-width: 100% !important;
        height: 30px !important;
        min-height: 30px !important;
        margin: 0 !important;
        padding: 0 24px 0 0 !important;
        color: #5f5c57 !important;
        background-color: transparent !important;
        border: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        font-family:
          var(--font-body-family),
          Arial,
          sans-serif !important;
        font-size: 13px !important;
        line-height: 1.3 !important;
        cursor: pointer !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__variant:hover {
        color: #1d5637 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__variant:focus {
        outline:
          2px solid rgba(29, 86, 55, 0.16) !important;
        outline-offset: 2px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__single-variant {
        display: block !important;
        color: #5f5c57 !important;
        font-size: 13px !important;
        line-height: 1.3 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__add {
        position: relative !important;
        grid-column: 3 !important;
        grid-row: 2 !important;
        align-self: end !important;
        justify-self: end !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: auto !important;
        min-width: 76px !important;
        min-height: 34px !important;
        margin: 0 !important;
        padding: 7px 14px !important;
        color: #1d5637 !important;
        background: #eaf1eb !important;
        border: 0 !important;
        border-radius: 999px !important;
        box-shadow: none !important;
        font-family:
          var(--font-body-family),
          Arial,
          sans-serif !important;
        font-size: 13px !important;
        font-weight: 700 !important;
        line-height: 1 !important;
        white-space: nowrap !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__add:hover:not(:disabled) {
        color: #ffffff !important;
        background: #1d5637 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__add:focus-visible {
        outline: 2px solid #1d5637 !important;
        outline-offset: 2px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__add:disabled {
        opacity: 0.7 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-card__add-spinner {
        border-color:
          rgba(29, 86, 55, 0.25) !important;
        border-top-color: #1d5637 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__footer {
        margin: 15px 0 0 !important;
        padding: 0 !important;
        border: 0 !important;
        text-align: center !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__view-all {
        color: #1d5637 !important;
        font-size: 14px !important;
        font-weight: 700 !important;
        text-underline-offset: 3px !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__view-all:hover {
        color: #17472e !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-carousel__status {
        margin: 8px 0 0 !important;
        color: #a02727 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
      }

      html body cart-drawer.is-empty
        .gt-empty-cart-login {
        width: auto !important;
        margin: 18px 18px 0 !important;
        padding: 18px !important;
        background: #ffffff !important;
        border:
          1px solid rgba(53, 48, 39, 0.1) !important;
        border-radius: 12px !important;
        box-shadow: none !important;
        text-align: center !important;
      }

      /* ==================================================
         DISPATCH MESSAGE

         Used by:
         - Desktop cart drawer
         - Mobile cart drawer
         - Desktop full cart page
         - Mobile full cart page
         ================================================== */

      html body .gt-dispatch-message {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 6px !important;
        width: 100% !important;
        max-width: 100% !important;
        min-height: 20px !important;
        margin: 7px 0 0 !important;
        padding: 0 4px !important;
        color: #5f5c57 !important;
        font-family:
          var(--font-body-family),
          Arial,
          sans-serif !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        letter-spacing: 0 !important;
        text-align: center !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: clip !important;
      }

      html body .gt-dispatch-message[hidden] {
        display: none !important;
      }

      html body .gt-dispatch-message__icon {
        display: inline-flex !important;
        flex: 0 0 auto !important;
        align-items: center !important;
        justify-content: center !important;
        width: 14px !important;
        height: 14px !important;
        color: #1d5637 !important;
      }

      html body .gt-dispatch-message__icon svg {
        display: block !important;
        width: 14px !important;
        height: 14px !important;
      }

      html body .gt-dispatch-message__text {
        display: block !important;
        min-width: 0 !important;
        white-space: nowrap !important;
      }

      html body cart-drawer
        .gt-dispatch-message {
        margin-top: 6px !important;
        font-size: 13px !important;
      }

      html body #main-cart-footer
        .gt-dispatch-message,
      html body .gt-cart-summary-card
        .gt-dispatch-message {
        margin-top: 9px !important;
        font-size: 14px !important;
      }

      html body cart-drawer
        .gt-dispatch-message
        + .gt-cart-payment-icons,
      html body #main-cart-footer
        .gt-dispatch-message
        + .gt-cart-page-payment-icons,
      html body .gt-cart-summary-card
        .gt-dispatch-message
        + .gt-cart-page-payment-icons {
        margin-top: 6px !important;
      }

      /* ==================================================
         MOBILE
         ================================================== */

      @media screen and (max-width: 749px) {
        html body cart-drawer.is-empty
          .cart-drawer__empty-content {
          padding:
            18px
            0
            calc(24px + env(safe-area-inset-bottom)) !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-hero {
          margin:
            0
            14px
            18px !important;
          padding:
            20px
            16px
            22px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-hero__heading {
          font-size: 22px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-hero__text {
          font-size: 15px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-hero__button.button {
          min-height: 46px !important;
          font-size: 15px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-carousel {
          padding:
            18px
            14px
            17px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-carousel__heading {
          font-size: 18px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-carousel__subheading {
          font-size: 12px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-carousel__counter {
          min-width: 54px !important;
          min-height: 32px !important;
          font-size: 13px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-carousel__arrow {
          width: 32px !important;
          min-width: 32px !important;
          height: 32px !important;
          min-height: 32px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card {
          flex-basis:
            calc(100vw - 68px) !important;
          width:
            calc(100vw - 68px) !important;
          max-width: 380px !important;
          min-width: 290px !important;
          grid-template-columns:
            82px
            minmax(0, 1fr)
            auto !important;
          gap: 7px 10px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card__title {
          font-size: 15px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card__price strong,
        html body cart-drawer.is-empty
          .gt-empty-cart-card__selected-price {
          font-size: 16px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card__variant {
          font-size: 12.5px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-login {
          margin-right: 14px !important;
          margin-left: 14px !important;
        }

        html body .gt-dispatch-message,
        html body #main-cart-footer
          .gt-dispatch-message,
        html body .gt-cart-summary-card
          .gt-dispatch-message {
          gap: 5px !important;
          min-height: 19px !important;
          margin-top: 7px !important;
          padding-right: 2px !important;
          padding-left: 2px !important;
          font-size: 12.5px !important;
        }

        html body .gt-dispatch-message__icon,
        html body .gt-dispatch-message__icon svg {
          width: 13px !important;
          height: 13px !important;
        }
      }

      @media screen and (max-width: 390px) {
        html body cart-drawer.is-empty
          .gt-empty-cart-carousel__header {
          align-items: flex-start !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card {
          flex-basis:
            calc(100vw - 58px) !important;
          width:
            calc(100vw - 58px) !important;
          min-width: 280px !important;
          grid-template-columns:
            76px
            minmax(0, 1fr)
            auto !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card__image-link {
          width: 76px !important;
          height: 76px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card__title {
          font-size: 14px !important;
        }

        html body cart-drawer.is-empty
          .gt-empty-cart-card__add {
          min-width: 68px !important;
          padding-right: 11px !important;
          padding-left: 11px !important;
          font-size: 12.5px !important;
        }

        html body .gt-dispatch-message,
        html body #main-cart-footer
          .gt-dispatch-message,
        html body .gt-cart-summary-card
          .gt-dispatch-message {
          gap: 4px !important;
          font-size: 12px !important;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        html body cart-drawer.is-empty
          .gt-empty-cart-carousel__viewport {
          scroll-behavior: auto !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /* ======================================================
     SHOPIFY SERVER CLOCK
     ====================================================== */

  function getShopifyCartClockUrl() {
    const root =
      window.Shopify
        ?.routes
        ?.root ||
      window.routes
        ?.root ||
      '/';

    const normalisedRoot =
      root.endsWith('/')
        ? root
        : `${root}/`;

    const url =
      new URL(
        `${normalisedRoot}cart.js`,
        window.location.origin
      );

    /*
     * A unique query value plus cache: no-store prevents a
     * previously cached response from supplying an old Date
     * header.
     */
    url.searchParams.set(
      '_gt_clock',
      String(Date.now())
    );

    return url.toString();
  }

  function getAccurateNow() {
    if (!serverClockSynchronised) {
      return new Date();
    }

    /*
     * Continue the synchronised clock from performance.now(),
     * which is monotonic and is not affected if the customer
     * manually changes the device's wall clock after loading
     * the page.
     */
    return new Date(
      serverClockBaseTimeMs +
        (
          window.performance.now() -
          serverClockBasePerformanceMs
        )
    );
  }

  async function synchroniseServerClock({
    force = false,
  } = {}) {
    const nowPerformance =
      window.performance.now();

    if (
      !force &&
      serverClockSynchronised &&
      nowPerformance -
        serverClockLastSyncedPerformanceMs <
        SERVER_CLOCK_MAX_AGE_MS
    ) {
      return true;
    }

    if (serverClockSyncPromise) {
      return serverClockSyncPromise;
    }

    serverClockSyncPromise =
      (async () => {
        const requestStartedAt =
          window.performance.now();

        try {
          const response =
            await fetch(
              getShopifyCartClockUrl(),
              {
                method: 'GET',
                cache: 'no-store',
                credentials: 'same-origin',

                headers: {
                  Accept:
                    'application/json',

                  'X-Requested-With':
                    'XMLHttpRequest',
                },
              }
            );

          if (!response.ok) {
            throw new Error(
              `Shopify clock request failed with status ${response.status}`
            );
          }

          const serverDateHeader =
            response.headers.get(
              'Date'
            );

          const serverDateMs =
            Date.parse(
              serverDateHeader || ''
            );

          if (
            !Number.isFinite(
              serverDateMs
            )
          ) {
            throw new Error(
              'Shopify clock response did not include a valid Date header'
            );
          }

          const responseReceivedAt =
            window.performance.now();

          const roundTripMs =
            Math.max(
              0,
              responseReceivedAt -
                requestStartedAt
            );

          /*
           * The HTTP Date header has whole-second precision.
           * Adding half the measured round-trip time gives a
           * sensible estimate of the server clock at the
           * moment the response reaches the browser.
           */
          const estimatedServerAtReceipt =
            serverDateMs +
            roundTripMs / 2;

          serverClockBaseTimeMs =
            estimatedServerAtReceipt;

          serverClockBasePerformanceMs =
            responseReceivedAt;

          serverClockLastSyncedPerformanceMs =
            responseReceivedAt;

          serverClockSynchronised =
            true;

          return true;
        } catch (error) {
          /*
           * Keep the most recent successful server-clock
           * baseline. If no successful sync has occurred, the
           * customer's device clock is used as the fallback.
           */
          console.warn(
            'Goodness Tea dispatch clock could not synchronise with Shopify. Using the available clock fallback.',
            error
          );

          return false;
        } finally {
          serverClockSyncPromise =
            null;
        }
      })();

    return serverClockSyncPromise;
  }

  function refreshServerClock({
    force = false,
  } = {}) {
    return synchroniseServerClock({
      force,
    }).finally(() => {
      updateDispatchMessages();
    });
  }

  /* ======================================================
     DISPATCH DATE AND WORDING
     ====================================================== */

  function getMelbourneDateTime(
    date = getAccurateNow()
  ) {
    const values = {};

    MELBOURNE_DATE_TIME_FORMATTER
      .formatToParts(date)
      .forEach(({ type, value }) => {
        if (type !== 'literal') {
          values[type] = value;
        }
      });

    return {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      hour: Number(values.hour) % 24,
      minute: Number(values.minute),
      second: Number(values.second),
    };
  }

  /*
   * Calendar calculations use UTC only as a neutral container.
   *
   * The year, month and date have already been calculated in
   * Melbourne time before this function is called.
   */
  function toCalendarDate({
    year,
    month,
    day,
  }) {
    return new Date(
      Date.UTC(year, month - 1, day)
    );
  }

  function addCalendarDays(date, days) {
    const result = new Date(date.getTime());

    result.setUTCDate(
      result.getUTCDate() + days
    );

    return result;
  }

  function getCalendarDateKey(date) {
    const year = date.getUTCFullYear();

    const month = String(
      date.getUTCMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getUTCDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  function isDispatchDate(date) {
    const weekday = date.getUTCDay();

    const isWeekday =
      weekday >= 1 && weekday <= 5;

    return (
      isWeekday &&
      !PUBLIC_HOLIDAYS.has(
        getCalendarDateKey(date)
      )
    );
  }

  function getNextDispatchDate(startDate) {
    let candidate =
      addCalendarDays(startDate, 1);

    /*
     * Three weeks is far longer than any relevant run of
     * weekends and Victorian public holidays.
     */
    for (
      let index = 0;
      index < 21;
      index += 1
    ) {
      if (isDispatchDate(candidate)) {
        return candidate;
      }

      candidate =
        addCalendarDays(candidate, 1);
    }

    return candidate;
  }

  function getCalendarDayDifference(
    fromDate,
    toDate
  ) {
    return Math.round(
      (
        toDate.getTime() -
        fromDate.getTime()
      ) / 86400000
    );
  }

  function getDispatchMessageText(
    now = getAccurateNow()
  ) {
    const melbourneNow =
      getMelbourneDateTime(now);

    const today =
      toCalendarDate(melbourneNow);

    /*
     * Orders always dispatch on the next valid Melbourne
     * business day. There is no same-day dispatch promise
     * and no time-of-day cutoff.
     */
    const nextDispatchDate =
      getNextDispatchDate(today);

    const dayDifference =
      getCalendarDayDifference(
        today,
        nextDispatchDate
      );

    /*
     * Examples:
     *
     * Monday to Thursday normally show "Ships tomorrow".
     * Sunday normally shows "Ships tomorrow".
     * A public holiday followed by a normal weekday also
     * shows "Ships tomorrow".
     */
    if (dayDifference === 1) {
      return 'Ships tomorrow';
    }

    /*
     * Examples:
     *
     * Friday and Saturday normally show "Ships Monday".
     * Long weekends and consecutive public holidays name
     * the next actual dispatch weekday.
     */
    return `Ships ${
      WEEKDAY_NAMES[
        nextDispatchDate.getUTCDay()
      ]
    }`;
  }

  function createDispatchMessage() {
    const message =
      document.createElement('div');

    message.className =
      'gt-dispatch-message';

    message.dataset.gtDispatchMessage = '';

    message.setAttribute(
      'role',
      'status'
    );

    message.setAttribute(
      'aria-live',
      'polite'
    );

    message.setAttribute(
      'title',
      'Dispatch timing is calculated using Shopify server time and Melbourne dates.'
    );

    message.innerHTML = `
      <span
        class="gt-dispatch-message__icon"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="12"
            cy="12"
            r="8.5"
          ></circle>

          <path
            d="M12 7.5V12l3 2"
          ></path>
        </svg>
      </span>

      <span
        class="gt-dispatch-message__text"
        data-gt-dispatch-message-text
      ></span>
    `;

    return message;
  }

  function getCheckoutButtons(
    root = document
  ) {
    return Array.from(
      root.querySelectorAll(
        `${
          SELECTORS.drawerCheckout
        }, ${
          SELECTORS.cartPageCheckout
        }`
      )
    );
  }

  function ensureDispatchMessages(
    root = document
  ) {
    getCheckoutButtons(root).forEach(
      (checkoutButton) => {
        let message =
          checkoutButton.nextElementSibling;

        if (
          !message?.matches(
            SELECTORS.dispatchMessage
          )
        ) {
          message =
            createDispatchMessage();

          checkoutButton
            .insertAdjacentElement(
              'afterend',
              message
            );
        }
      }
    );
  }

  function updateDispatchMessages(
    root = document
  ) {
    ensureDispatchMessages(root);

    const text =
      getDispatchMessageText();

    root
      .querySelectorAll(
        SELECTORS.dispatchMessage
      )
      .forEach((message) => {
        const textElement =
          message.querySelector(
            SELECTORS.dispatchText
          );

        if (
          textElement &&
          textElement.textContent !== text
        ) {
          textElement.textContent = text;
        }
      });
  }

  function startDispatchClock() {
    /*
     * Render immediately using the latest available clock.
     * The message is refreshed again as soon as Shopify's
     * server time has been obtained.
     */
    updateDispatchMessages();

    refreshServerClock({
      force: true,
    });

    window.clearInterval(
      dispatchTimer
    );

    window.clearInterval(
      serverClockResyncTimer
    );

    /*
     * Updating every 15 seconds means an open page changes
     * shortly after midnight when the Melbourne date changes.
     */
    dispatchTimer =
      window.setInterval(() => {
        updateDispatchMessages();
      }, 15000);

    /*
     * Re-synchronise periodically so long-running sessions do
     * not drift and manual changes to the device clock cannot
     * permanently affect the dispatch promise.
     */
    serverClockResyncTimer =
      window.setInterval(() => {
        refreshServerClock({
          force: true,
        });
      }, SERVER_CLOCK_RESYNC_INTERVAL_MS);
  }

  /* ======================================================
     EMPTY-CART PRICE CORRECTION
     ====================================================== */

  function getSelectedVariantPrice(
    select
  ) {
    if (!select) {
      return '';
    }

    const selectedOption =
      select.options[
        select.selectedIndex
      ];

    if (!selectedOption) {
      return '';
    }

    if (
      selectedOption.dataset.price
    ) {
      return selectedOption
        .dataset
        .price
        .trim();
    }

    const optionText =
      selectedOption
        .textContent
        .replace(/\s+/g, ' ')
        .trim();

    const separatorIndex =
      optionText.lastIndexOf('·');

    if (separatorIndex !== -1) {
      return optionText
        .slice(separatorIndex + 1)
        .trim()
        .replace(/\s+/g, '');
    }

    const priceMatches =
      optionText.match(
        /(?:A\$|\$)\s?[\d,]+(?:\.\d{2})?/g
      );

    if (priceMatches?.length) {
      return priceMatches[
        priceMatches.length - 1
      ].replace(/\s+/g, '');
    }

    return '';
  }

  function normaliseCardPrice(card) {
    if (!card) {
      return;
    }

    const priceContainer =
      card.querySelector(
        '.gt-empty-cart-card__price'
      );

    if (!priceContainer) {
      return;
    }

    const currentPrice =
      priceContainer.querySelector(
        'strong'
      );

    const crossedOutPrices =
      priceContainer.querySelectorAll(
        's'
      );

    crossedOutPrices.forEach(
      (crossedOutPrice) => {
        crossedOutPrice.hidden = true;

        crossedOutPrice.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    );

    if (!currentPrice) {
      return;
    }

    const variantSelect =
      card.querySelector(
        SELECTORS.variant
      );

    const selectedVariantPrice =
      getSelectedVariantPrice(
        variantSelect
      );

    if (selectedVariantPrice) {
      currentPrice.textContent =
        selectedVariantPrice;
    } else {
      currentPrice.textContent =
        currentPrice.textContent
          .replace(/^From\s*/i, '')
          .replace(/\s+/g, '')
          .trim();
    }

    currentPrice.classList.add(
      'gt-empty-cart-card__selected-price'
    );

    priceContainer.setAttribute(
      'aria-live',
      'polite'
    );
  }

  function normaliseCarouselPrices(
    carousel
  ) {
    carousel
      .querySelectorAll(
        SELECTORS.card
      )
      .forEach(
        normaliseCardPrice
      );
  }

  /* ======================================================
     EMPTY-CART CAROUSEL CONTROLS
     ====================================================== */

  function getCards(carousel) {
    return Array.from(
      carousel.querySelectorAll(
        SELECTORS.card
      )
    );
  }

  function getNearestCardIndex(
    viewport,
    cards
  ) {
    if (!cards.length) {
      return 0;
    }

    const currentScroll =
      viewport.scrollLeft;

    let nearestIndex = 0;

    let nearestDistance =
      Number.POSITIVE_INFINITY;

    cards.forEach(
      (card, index) => {
        const distance = Math.abs(
          card.offsetLeft -
          currentScroll
        );

        if (
          distance <
          nearestDistance
        ) {
          nearestDistance =
            distance;

          nearestIndex = index;
        }
      }
    );

    return nearestIndex;
  }

  function updateCarousel(carousel) {
    const viewport =
      carousel.querySelector(
        SELECTORS.viewport
      );

    const counter =
      carousel.querySelector(
        SELECTORS.counter
      );

    const arrows = Array.from(
      carousel.querySelectorAll(
        SELECTORS.arrow
      )
    );

    const cards =
      getCards(carousel);

    if (
      !viewport ||
      !cards.length
    ) {
      if (counter) {
        counter.textContent =
          '0 / 0';
      }

      arrows.forEach((arrow) => {
        arrow.disabled = true;
      });

      return;
    }

    const currentIndex =
      getNearestCardIndex(
        viewport,
        cards
      );

    const maximumScroll = Math.max(
      0,
      viewport.scrollWidth -
        viewport.clientWidth
    );

    if (counter) {
      counter.textContent =
        `${
          currentIndex + 1
        } / ${
          cards.length
        }`;
    }

    arrows.forEach((arrow) => {
      const direction = Number(
        arrow
          .dataset
          .emptyCartScroll || 0
      );

      if (direction < 0) {
        arrow.disabled =
          currentIndex <= 0 ||
          viewport.scrollLeft <= 2;
      } else {
        arrow.disabled =
          currentIndex >=
            cards.length - 1 ||
          viewport.scrollLeft >=
            maximumScroll - 2;
      }
    });
  }

  function initialiseCarousel(
    carousel
  ) {
    const viewport =
      carousel.querySelector(
        SELECTORS.viewport
      );

    if (!viewport) {
      return;
    }

    normaliseCarouselPrices(
      carousel
    );

    if (
      viewport
        .dataset
        .emptyCartInitialised !==
      'true'
    ) {
      viewport
        .dataset
        .emptyCartInitialised =
        'true';

      viewport.addEventListener(
        'scroll',
        () => {
          window
            .requestAnimationFrame(
              () => {
                updateCarousel(
                  carousel
                );
              }
            );
        },
        {
          passive: true,
        }
      );
    }

    updateCarousel(carousel);
  }

  function initialiseAllCarousels(
    root = document
  ) {
    injectStyles();

    root
      .querySelectorAll(
        SELECTORS.carousel
      )
      .forEach(
        initialiseCarousel
      );
  }

  function getCarouselStep(
    carousel
  ) {
    const viewport =
      carousel.querySelector(
        SELECTORS.viewport
      );

    const card =
      carousel.querySelector(
        SELECTORS.card
      );

    const track =
      viewport?.firstElementChild;

    if (
      !viewport ||
      !card
    ) {
      return 280;
    }

    let gap = 12;

    if (track) {
      const trackStyles =
        window.getComputedStyle(
          track
        );

      const parsedGap =
        Number.parseFloat(
          trackStyles.columnGap ||
          trackStyles.gap
        );

      if (
        Number.isFinite(
          parsedGap
        )
      ) {
        gap = parsedGap;
      }
    }

    return (
      card
        .getBoundingClientRect()
        .width +
      gap
    );
  }

  function handleCarouselArrow(
    button
  ) {
    const carousel =
      button.closest(
        SELECTORS.carousel
      );

    const viewport =
      carousel?.querySelector(
        SELECTORS.viewport
      );

    if (
      !carousel ||
      !viewport ||
      button.disabled
    ) {
      return;
    }

    const direction = Number(
      button
        .dataset
        .emptyCartScroll || 0
    );

    viewport.scrollBy({
      left:
        direction *
        getCarouselStep(
          carousel
        ),

      behavior: 'smooth',
    });
  }

  /* ======================================================
     STATUS AND BUTTON LOADING
     ====================================================== */

  function showStatus(
    carousel,
    message,
    isError = false
  ) {
    const status =
      carousel?.querySelector(
        SELECTORS.status
      );

    if (!status) {
      return;
    }

    status.textContent =
      message;

    status.classList.toggle(
      'is-error',
      isError
    );
  }

  function setAddButtonLoading(
    button,
    loading
  ) {
    button.classList.toggle(
      'is-loading',
      loading
    );

    button.disabled =
      loading;

    button.setAttribute(
      'aria-busy',
      loading
        ? 'true'
        : 'false'
    );
  }

  function getSelectedVariantId(
    button
  ) {
    const card =
      button.closest(
        SELECTORS.card
      );

    const variantSelect =
      card?.querySelector(
        SELECTORS.variant
      );

    if (variantSelect?.value) {
      return variantSelect.value;
    }

    return (
      button
        .dataset
        .variantId || ''
    );
  }

  /* ======================================================
     SHOPIFY CART REQUEST
     ====================================================== */

  function getCartSections(
    drawer
  ) {
    if (
      drawer &&
      typeof drawer
        .getSectionsToRender ===
        'function'
    ) {
      const sections =
        drawer
          .getSectionsToRender()
          .map(
            (section) =>
              section.id
          )
          .filter(Boolean);

      if (sections.length) {
        return sections;
      }
    }

    return [
      'cart-drawer',
      'cart-icon-bubble',
    ];
  }

  function getFetchConfiguration(
    formData
  ) {
    let configuration;

    if (
      typeof window.fetchConfig ===
      'function'
    ) {
      configuration =
        window.fetchConfig(
          'javascript'
        );

      if (
        configuration.headers
      ) {
        delete configuration
          .headers[
            'Content-Type'
          ];
      }
    } else if (
      typeof fetchConfig ===
      'function'
    ) {
      configuration =
        fetchConfig(
          'javascript'
        );

      if (
        configuration.headers
      ) {
        delete configuration
          .headers[
            'Content-Type'
          ];
      }
    } else {
      configuration = {
        method: 'POST',

        headers: {
          Accept:
            'application/json',

          'X-Requested-With':
            'XMLHttpRequest',
        },
      };
    }

    configuration.method =
      'POST';

    configuration.body =
      formData;

    return configuration;
  }

  async function addEmptyCartProduct(
    button
  ) {
    if (button.disabled) {
      return;
    }

    const carousel =
      button.closest(
        SELECTORS.carousel
      );

    const variantId =
      getSelectedVariantId(
        button
      );

    if (!variantId) {
      showStatus(
        carousel,
        'Please choose a product option.',
        true
      );

      return;
    }

    setAddButtonLoading(
      button,
      true
    );

    showStatus(
      carousel,
      ''
    );

    const drawer =
      document.querySelector(
        'cart-drawer'
      );

    const formData =
      new FormData();

    formData.append(
      'id',
      variantId
    );

    formData.append(
      'quantity',
      '1'
    );

    formData.append(
      'sections',
      getCartSections(
        drawer
      ).join(',')
    );

    formData.append(
      'sections_url',
      window.location.pathname
    );

    const cartAddUrl =
      window.routes
        ?.cart_add_url ||
      `${
        window.Shopify
          ?.routes
          ?.root || '/'
      }cart/add.js`;

    try {
      const response =
        await fetch(
          cartAddUrl,
          getFetchConfiguration(
            formData
          )
        );

      const cartResponse =
        await response.json();

      if (
        !response.ok ||
        cartResponse.status
      ) {
        throw new Error(
          cartResponse.description ||
          cartResponse.message ||
          'This product could not be added.'
        );
      }

      if (
        drawer &&
        typeof drawer
          .renderContents ===
          'function' &&
        cartResponse.sections
      ) {
        drawer.renderContents(
          cartResponse
        );
      } else {
        window.location.assign(
          window.routes
            ?.cart_url ||
          '/cart'
        );

        return;
      }

      if (
        typeof window.publish ===
          'function' &&
        window.PUB_SUB_EVENTS
          ?.cartUpdate
      ) {
        window.publish(
          window
            .PUB_SUB_EVENTS
            .cartUpdate,

          {
            source:
              'gt-empty-cart-browse',

            productVariantId:
              variantId,

            cartData:
              cartResponse,
          }
        );
      }
    } catch (error) {
      console.error(
        'Goodness Tea empty-cart add error:',
        error
      );

      showStatus(
        carousel,

        error instanceof Error
          ? error.message
          : 'This product could not be added. Please try again.',

        true
      );
    } finally {
      if (button.isConnected) {
        setAddButtonLoading(
          button,
          false
        );
      }
    }
  }

  /* ======================================================
     EVENTS
     ====================================================== */

  document.addEventListener(
    'click',
    (event) => {
      if (
        !(
          event.target
          instanceof Element
        )
      ) {
        return;
      }

      const arrow =
        event.target.closest(
          SELECTORS.arrow
        );

      if (arrow) {
        event.preventDefault();

        handleCarouselArrow(
          arrow
        );

        return;
      }

      const addButton =
        event.target.closest(
          SELECTORS.addButton
        );

      if (addButton) {
        event.preventDefault();
        event.stopPropagation();

        addEmptyCartProduct(
          addButton
        );
      }
    }
  );

  document.addEventListener(
    'change',
    (event) => {
      if (
        !(
          event.target
          instanceof Element
        )
      ) {
        return;
      }

      const variantSelect =
        event.target.closest(
          SELECTORS.variant
        );

      if (!variantSelect) {
        return;
      }

      const card =
        variantSelect.closest(
          SELECTORS.card
        );

      const addButton =
        card?.querySelector(
          SELECTORS.addButton
        );

      if (addButton) {
        addButton
          .dataset
          .variantId =
          variantSelect.value;
      }

      normaliseCardPrice(
        card
      );
    }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) {
        refreshServerClock();
      }
    }
  );

  window.addEventListener(
    'focus',
    () => {
      refreshServerClock();
    }
  );

  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(
        resizeTimer
      );

      resizeTimer =
        window.setTimeout(
          () => {
            initialiseAllCarousels();

            updateDispatchMessages();
          },
          100
        );
    },
    {
      passive: true,
    }
  );

  /* ======================================================
     HANDLE DYNAMIC CART-DRAWER AND CART-PAGE REPLACEMENT
     ====================================================== */

  const observer =
    new MutationObserver(
      (mutations) => {
        let shouldInitialiseCarousels =
          false;

        let shouldRefreshDispatchMessages =
          false;

        for (
          const mutation
          of mutations
        ) {
          for (
            const addedNode
            of mutation.addedNodes
          ) {
            if (
              !(
                addedNode
                instanceof Element
              )
            ) {
              continue;
            }

            if (
              addedNode.matches?.(
                SELECTORS.carousel
              ) ||
              addedNode.querySelector?.(
                SELECTORS.carousel
              )
            ) {
              shouldInitialiseCarousels =
                true;
            }

            if (
              addedNode.matches?.(
                `${
                  SELECTORS
                    .drawerCheckout
                }, ${
                  SELECTORS
                    .cartPageCheckout
                }`
              ) ||
              addedNode.querySelector?.(
                `${
                  SELECTORS
                    .drawerCheckout
                }, ${
                  SELECTORS
                    .cartPageCheckout
                }`
              )
            ) {
              shouldRefreshDispatchMessages =
                true;
            }

            if (
              shouldInitialiseCarousels &&
              shouldRefreshDispatchMessages
            ) {
              break;
            }
          }

          if (
            shouldInitialiseCarousels &&
            shouldRefreshDispatchMessages
          ) {
            break;
          }
        }

        if (
          shouldInitialiseCarousels ||
          shouldRefreshDispatchMessages
        ) {
          window.requestAnimationFrame(
            () => {
              if (
                shouldInitialiseCarousels
              ) {
                initialiseAllCarousels();
              }

              updateDispatchMessages();
            }
          );
        }
      }
    );

  function start() {
    injectStyles();

    initialiseAllCarousels();

    startDispatchClock();

    if (document.body) {
      observer.observe(
        document.body,
        {
          childList: true,
          subtree: true,
        }
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }
})();